import React, { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import {
  HandCoins, Search, RefreshCw, Download, AlertTriangle, CheckCircle2, Loader2, X,
} from 'lucide-react';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { formatDate } from '../../src/lib/utils';
import { formatCurrency } from '../../src/lib/comissaoUtils';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import { buildSheet, downloadWorkbook, fileStamp } from '../../src/lib/excelExport';
import { podeAuditarPagamentos } from './AuditoriaPagamentosView';
import {
  diasDeAtraso, estaVencido, precisaSegundaVia, ordenarPorVencimento, somarValores, motivoDaFalha,
} from '../../src/lib/repasseEquatorial';

/** Item da fila com os campos da fatura que a tela precisa, trazidos por embed. */
interface ItemManual {
  id: string;
  fatura_id: number;
  uc_codigo: string;
  mes_referencia: string;
  valor_equatorial: number;
  linha_digitavel: string | null;
  pix_copia_cola: string | null;
  /** 'manual' = a parceira paga por fora, por desenho. 'falhou' = o pagamento automático desistiu. */
  status: string;
  error_details: string | null;
  created_at: string;
  faturas: {
    cliente_nome: string | null;
    data_vencimento: string | null;
    data_pgto_boleto: string | null;
  } | null;
}

const RepasseEquatorialManualView: React.FC = () => {
  const { profile } = useAuth();
  const { addToast } = useToast();

  // Mesma regra do FaturaFAB, que é por onde se chega aqui. Se divergir do FAB, o usuário
  // vê o botão e leva um redirect na cara.
  const hasAccess = podeAuditarPagamentos(profile);

  const [itens, setItens] = useState<ItemManual[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [modalAberto, setModalAberto] = useState(false);
  const [dataBaixa, setDataBaixa] = useState(() => new Date().toISOString().split('T')[0]);
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);

  const fetchItens = async () => {
    setLoading(true);
    try {
      // O PostgREST corta em 1000 linhas por resposta — pagina com .range() até esgotar,
      // no mesmo padrão da AuditoriaBoletoUnicoView.
      let todos: ItemManual[] = [];
      let from = 0;
      const passo = 1000;
      while (true) {
        const { data, error } = await supabase
          .from('pagamento_equatorial_fila')
          .select('id, fatura_id, uc_codigo, mes_referencia, valor_equatorial, linha_digitavel, pix_copia_cola, status, error_details, created_at, faturas(cliente_nome, data_vencimento, data_pgto_boleto)')
          // 'falhou' entrou em 02/09/2026: quando o pagamento automático desiste, o cron
          // não volta a tentar e a conta da distribuidora fica sem ninguém olhando. Antes
          // esses itens não apareciam em tela nenhuma.
          .in('status', ['manual', 'falhou'])
          .order('created_at', { ascending: true })
          .range(from, from + passo - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        todos = todos.concat(data as unknown as ItemManual[]);
        if (data.length < passo) break;
        from += passo;
      }
      setItens(todos);
      setSelecionados(new Set());
    } catch (error) {
      console.error('Erro ao carregar a fila de repasse manual:', error);
      addToast('Erro ao carregar os repasses à Equatorial.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasAccess) fetchItens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess]);

  // Ordena pelo vencimento da distribuidora, mais antigo primeiro: é a ordem em que o
  // dinheiro precisa sair. Item sem vencimento vai para o fim.
  const filtrados = useMemo(() => {
    const termo = searchTerm.trim().toLowerCase();
    const lista = termo
      ? itens.filter(i => `${i.faturas?.cliente_nome ?? ''} ${i.uc_codigo} ${i.mes_referencia}`.toLowerCase().includes(termo))
      : itens;
    return ordenarPorVencimento(lista);
  }, [itens, searchTerm]);

  const totalDevido = useMemo(() => somarValores(filtrados), [filtrados]);
  const totalVencido = useMemo(() => filtrados.filter(i => estaVencido(i)).length, [filtrados]);
  const totalSemCodigo = useMemo(() => filtrados.filter(precisaSegundaVia).length, [filtrados]);
  const totalFalhou = useMemo(() => filtrados.filter(i => i.status === 'falhou').length, [filtrados]);
  const valorSelecionado = useMemo(
    () => somarValores(filtrados.filter(i => selecionados.has(i.id))),
    [filtrados, selecionados],
  );

  const alternarSelecao = (id: string) => {
    setSelecionados(prev => {
      const proximo = new Set(prev);
      if (proximo.has(id)) proximo.delete(id); else proximo.add(id);
      return proximo;
    });
  };

  const alternarTodos = () => {
    setSelecionados(prev => (prev.size === filtrados.length ? new Set() : new Set(filtrados.map(i => i.id))));
  };

  const confirmarBaixa = async () => {
    if (selecionados.size === 0) return;
    setSalvando(true);
    try {
      const { data, error } = await supabase.rpc('dar_baixa_repasse_equatorial', {
        p_ids: Array.from(selecionados),
        p_data: dataBaixa,
        p_observacao: observacao.trim() || null,
      });
      if (error) throw error;
      addToast(`Baixa registrada em ${data} repasse(s).`, 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'repasse_equatorial', acao: 'dar_baixa_repasse_equatorial', origem: 'acao' });
      setModalAberto(false);
      setObservacao('');
      await fetchItens();
    } catch (error: any) {
      console.error('Erro ao dar baixa:', error);
      addToast(error?.message || 'Erro ao registrar a baixa.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const exportarExcel = () => {
    const headers = ['Cliente', 'UC', 'Origem', 'Mês Referência', 'Valor Equatorial (R$)', 'Venc. distribuidora', 'Dias de atraso', 'Cliente pagou em', 'Código de barras'];
    const rows = filtrados.map(i => {
      const atraso = diasDeAtraso(i.faturas?.data_vencimento);
      return [
        i.faturas?.cliente_nome ?? '',
        i.uc_codigo,
        i.status === 'falhou'
          ? `Automático falhou${motivoDaFalha(i.error_details) ? ' — ' + motivoDaFalha(i.error_details) : ''}`
          : motivoDaFalha(i.error_details)?.startsWith('Valor divergente')
            ? motivoDaFalha(i.error_details)
            : 'Parceira paga por fora',
        i.mes_referencia,
        Number(i.valor_equatorial) || 0,
        i.faturas?.data_vencimento ? formatDate(i.faturas.data_vencimento) : '',
        atraso ?? '',
        i.faturas?.data_pgto_boleto ? formatDate(i.faturas.data_pgto_boleto) : '',
        precisaSegundaVia(i) ? 'NÃO — pedir segunda via' : 'Sim',
      ];
    });
    const totals = [`TOTAL (${filtrados.length} repasses)`, '', '', '', totalDevido, '', '', '', ''];
    const ws = buildSheet(
      headers, rows,
      ['txt', 'txt', 'txt', 'txt', 'num', 'txt', 'num', 'txt', 'txt'],
      { totals, widths: [35, 16, 42, 14, 18, 18, 14, 16, 24] },
    );
    downloadWorkbook([{ name: 'Repasse Equatorial', ws }], `Repasse_Equatorial_Manual_${fileStamp()}.xlsx`);
  };

  if (!hasAccess) return <Navigate to="/financeiro/faturas" replace />;

  return (
    <div className="animate-fade-in space-y-6 pb-24">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-poppins font-bold dark:text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center shadow-lg shadow-primary/20">
              <HandCoins size={20} className="text-white" />
            </div>
            Repasse à Equatorial — manual
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-[52px]">
            Contas da distribuidora esperando ação humana: as que a parceira paga por fora e as que o pagamento automático não conseguiu fazer. O cliente já pagou o boleto — falta quitar a Equatorial e dar baixa aqui.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchItens} className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" title="Atualizar">
            <RefreshCw size={16} className="text-gray-500 dark:text-gray-400" />
          </button>
          <button onClick={exportarExcel} disabled={filtrados.length === 0} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors">
            <Download size={16} /> Exportar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">A pagar à distribuidora</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatCurrency(totalDevido)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{filtrados.length} repasse(s)</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Já vencidos</p>
          <p className={`text-2xl font-bold mt-1 ${totalVencido > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{totalVencido}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Automático falhou</p>
          <p className={`text-2xl font-bold mt-1 ${totalFalhou > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{totalFalhou}</p>
          <p className="text-xs text-gray-400 mt-0.5">o cron não vai tentar de novo</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Sem código de barras</p>
          <p className={`text-2xl font-bold mt-1 ${totalSemCodigo > 0 ? 'text-amber-500 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>{totalSemCodigo}</p>
          <p className="text-xs text-gray-400 mt-0.5">precisa de segunda via</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar por cliente, UC ou mês..."
            className="w-full pl-9 pr-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        {selecionados.size > 0 ? (
          <button onClick={() => setModalAberto(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors">
            <CheckCircle2 size={16} /> Dar baixa em {selecionados.size} ({formatCurrency(valorSelecionado)})
          </button>
        ) : null}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filtrados.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3" />
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Nenhum repasse esperando ação</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Tudo que o cliente pagou já foi acertado com a distribuidora.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <th className="px-4 py-3 w-10">
                    <input type="checkbox" checked={selecionados.size === filtrados.length && filtrados.length > 0} onChange={alternarTodos} className="rounded border-gray-300 text-primary focus:ring-primary/40" aria-label="Selecionar todos" />
                  </th>
                  <th className="px-4 py-3">Cliente / UC</th>
                  <th className="px-4 py-3">Origem</th>
                  <th className="px-4 py-3">Mês</th>
                  <th className="px-4 py-3 text-right">Valor</th>
                  <th className="px-4 py-3">Venc. distribuidora</th>
                  <th className="px-4 py-3">Cliente pagou</th>
                  <th className="px-4 py-3">Código de barras</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filtrados.map(item => {
                  const atraso = diasDeAtraso(item.faturas?.data_vencimento);
                  const vencido = estaVencido(item);
                  const semCodigo = precisaSegundaVia(item);
                  const motivo = motivoDaFalha(item.error_details);
                  return (
                    <tr key={item.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors ${selecionados.has(item.id) ? 'bg-primary/5' : ''}`}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selecionados.has(item.id)} onChange={() => alternarSelecao(item.id)} className="rounded border-gray-300 text-primary focus:ring-primary/40" aria-label={`Selecionar repasse da UC ${item.uc_codigo}`} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900 dark:text-white">{item.faturas?.cliente_nome || '—'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{item.uc_codigo}</div>
                      </td>
                      <td className="px-4 py-3">
                        {item.status === 'falhou' ? (
                          <>
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700">
                              <AlertTriangle size={12} /> automático falhou
                            </span>
                            {motivo ? (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs">{motivo}</div>
                            ) : null}
                          </>
                        ) : (
                          <>
                            {/* O gatilho manda para o manual o boleto que cobrou da Equatorial
                                um valor diferente da conta dela (fn_enfileirar_pagamento_equatorial). */}
                            {motivo?.startsWith('Valor divergente') ? (
                              <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700">
                                <AlertTriangle size={12} /> conferir valor
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-400">parceira paga por fora</span>
                            )}
                            {motivo ? (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs">{motivo}</div>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.mes_referencia}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">{formatCurrency(Number(item.valor_equatorial) || 0)}</td>
                      <td className="px-4 py-3">
                        <div className="text-gray-600 dark:text-gray-300">{item.faturas?.data_vencimento ? formatDate(item.faturas.data_vencimento) : '—'}</div>
                        {vencido ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 dark:text-red-400">
                            <AlertTriangle size={12} /> {atraso} dia(s) em atraso
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.faturas?.data_pgto_boleto ? formatDate(item.faturas.data_pgto_boleto) : '—'}</td>
                      <td className="px-4 py-3">
                        {semCodigo ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700">
                            <AlertTriangle size={12} /> pedir segunda via
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500 dark:text-gray-400">disponível</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalAberto ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !salvando && setModalAberto(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Dar baixa no repasse</h3>
              <button onClick={() => setModalAberto(false)} disabled={salvando} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Confirmando que a distribuidora já foi paga em <strong>{selecionados.size}</strong> repasse(s),
                  somando <strong>{formatCurrency(valorSelecionado)}</strong>.
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Isto só registra o que já aconteceu — o VigorHub não envia dinheiro nenhum aqui.
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Data do pagamento</label>
                <input type="date" value={dataBaixa} onChange={e => setDataBaixa(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Observação (opcional)</label>
                <textarea value={observacao} onChange={e => setObservacao(e.target.value)} rows={3} placeholder="Ex.: pago por PIX pela Sandra, comprovante no drive" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40" />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setModalAberto(false)} disabled={salvando} className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors">Cancelar</button>
              <button onClick={confirmarBaixa} disabled={salvando} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-dark disabled:opacity-60 transition-colors">
                {salvando ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                Confirmar baixa
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RepasseEquatorialManualView;
